import { EnvConfig, Media, MediaType, WorkflowQuestionConfig } from '@gripp/shared-logic';
import { FC, ReactNode } from 'react';
import { PRINT_SHEET_CLASS as C } from '../printStyles';

/**
 * Every question the app renders is authored with an auto-generated `name`
 * ("Question 4") and the text the operator actually reads in `instructions` --
 * the execution form titles each question from `instructions` for the same
 * reason. `name` is only a fallback for a question saved without any text.
 */
export const questionText = (config: WorkflowQuestionConfig): string =>
  config.instructions?.trim() || config.name;

/**
 * Instructional images are stored as `Media` and served from the media CDN by
 * id, not by the `url` on the record -- that field holds a `blob:` URL captured
 * in the authoring session, which resolves to nothing anywhere else.
 */
const printableImageUrl = (media: Media | undefined): string | undefined => {
  if (!media?.id || media.type !== MediaType.Image || !EnvConfig.mediaUrl) {
    return undefined;
  }
  return `${EnvConfig.mediaUrl}/images/${media.id}/sm.${media.extension}`;
};

type PrintQuestionBlockProps = {
  config: WorkflowQuestionConfig;
  number?: number;
  requiredLabel: string;
  children: ReactNode;
};

export const PrintQuestionBlock: FC<PrintQuestionBlockProps> = ({
  config,
  number,
  requiredLabel,
  children,
}) => {
  const imageUrl = printableImageUrl(config.media);

  return (
    <div className={`${C}__question`}>
      <div className={`${C}__questionHead`}>
        {number !== undefined ? (
          <span className={`${C}__questionNumber`}>{number}.</span>
        ) : null}
        <span className={`${C}__questionText`}>
          {questionText(config)}
          {config.required ? (
            <span className={`${C}__required`} title={requiredLabel}>
              {' *'}
            </span>
          ) : null}
        </span>
      </div>
      <div className={`${C}__answer`}>
        {imageUrl ? (
          <img
            className={`${C}__questionMedia`}
            src={imageUrl}
            // The image illustrates the question, so its accessible name is
            // the question's own text rather than empty (decorative).
            alt={questionText(config)}
            onError={(event) => {
              // An unreachable instructional image must not leave a broken-image
              // glyph on the form; the question itself is still answerable.
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : null}
        {children}
      </div>
    </div>
  );
};
